import { useEffect, useState } from "react";
import { Tooltip } from "react-tooltip";
import { skipToken } from "@reduxjs/toolkit/query";
import { InformationCircleIcon } from "@heroicons/react/24/solid";
import { Button, Description, Field, Input, Label, Switch } from "@headlessui/react";
import {
	CheckCircleIcon,
	ChevronDownIcon,
	ChevronRightIcon,
	XCircleIcon,
} from "@heroicons/react/24/outline";
import { useAppSelector } from "../../../app/hooks";
import { handleEnterKey, handleEnterSpaceKey } from "../../../app/keyboardUtils";
import {
	isAdminRole,
	isHigherThanDistrictAdmin,
	isDistrictOrSchoolAdmin,
	UserRole,
	roleDescriptions,
} from "../../../app/userSlice";
import { SimpleOption } from "../../../components/input/SimpleListbox";
import Select from "../../../components/input/Select";
import Password from "../../../components/input/Password";
import AlertMessage, { AlertLevel } from "../../../components/AlertMessage";
import RequiredField from "../../../components/RequiredField";
import { Loading } from "../../../components/Loading";
import NamedEntity from "../../../interfaces/NamedEntity";
import { useGetGradesQuery } from "../../lookup/lookupApiSlice";
import { useAddStudentsToCourseByAdminMutation } from "../../courses/coursesApiSlice";
import { useAddExistingTeacherMutation } from "../../courses/manageCourseApiSlice";
import { useAddUserToSchoolMutation } from "../../schools/schoolsApiSlice";
import {
	useGetDistrictsQuery,
	useGetSchoolsByDistrictIdMutation,
} from "../../districts/districtsApiSlice";
import {
	CreateUserViewModel,
	Ethnicity,
	Gender,
	useCreateUserMutation,
	useGetCreateUserInfoQuery,
	roleColors,
} from "../../users/usersApiSlice";
import { externalIdLabel } from "../usersUtil";

const YesNoOptions: SimpleOption[] = [
	{ value: "", label: "--" },
	{ value: "true", label: "Yes" },
	{ value: "false", label: "No" },
];

interface Props {
	roleOptions?: SimpleOption[]; //if not sent, the selectedRole will be a readonly field
	selectedRole: string;
	saveButtonText?: string;
	setSuccessMessage?: (message: string) => void;
	onClose: () => void;
	hideDistrictSchool?: boolean;
	courseId?: number;
	schoolId?: number;
	districtId?: number;
}

export default function AddUserForm(props: Props) {
	const { config } = useAppSelector((state) => state.site);
	const currentUserRole = useAppSelector((state) => state.user.role);
	const todayDate = new Date().toISOString().slice(0, 10);

	const roleOptions = props.roleOptions;
	const [districtOptions, setDistrictOptions] = useState<SimpleOption[]>([]);
	const [schoolOptions, setSchoolOptions] = useState<SimpleOption[]>([]);
	const [gradeOptions, setGradeOptions] = useState<SimpleOption[]>([]);
	const [genderOptions, setGenderOptions] = useState<SimpleOption[]>([]);
	const [ethnicityOptions, setEthnicityOptions] = useState<SimpleOption[]>([]);

	const [roleToCreate, setRoleToCreate] = useState<string>(props.selectedRole);

	const [active, setActive] = useState(true);
	const [allowUserImages, setAllowUserImages] = useState(true);
	const [fName, setFName] = useState("");
	const [lName, setLName] = useState("");
	const [externalId, setExternalId] = useState("");
	const [dateOfBirth, setDateOfBirth] = useState("");
	const [grade, setGrade] = useState<number>();
	const [phone, setPhone] = useState("");
	const [email, setEmail] = useState("");
	const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true);
	const [userName, setUserName] = useState("");
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [useGenerateCredentials, setUseGenerateCredentials] = useState(true);

	const [selectedDistrict, setSelectedDistrict] = useState(props.districtId ?? 0);
	const [selectedSchool, setSelectedSchool] = useState(props.schoolId ?? 0);
	const [schools, setSchools] = useState<NamedEntity[]>([]);

	const [showDemographicSection, setShowDemographicSection] = useState(false);
	const [ethnicity, setEthnicity] = useState("");
	const [gender, setGender] = useState("");
	const [economicallyDisadvantaged, setEconomicallyDisadvantaged] = useState<boolean>();
	const [limitedEnglish, setLimitedEnglish] = useState<boolean>();
	const [disability, setDisability] = useState<number>();

	const [showBadDataWarning, setShowBadDataWarning] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const [disableSave, setDisableSave] = useState(false);

	const gradesResult = useGetGradesQuery();
	const grades = gradesResult.data;

	const createUserInfoResult = useGetCreateUserInfoQuery(roleToCreate);
	const userToCreateInfo = createUserInfoResult.data;

	//only call for state and super admins logged in since district and school admins see a readonly field
	const getDistrictsResult = useGetDistrictsQuery(
		currentUserRole && isDistrictOrSchoolAdmin(currentUserRole) ? skipToken : undefined
	);
	const districts = getDistrictsResult.data;

	const [callGetSchoolsByDistrict] = useGetSchoolsByDistrictIdMutation();
	const [createUser] = useCreateUserMutation();
	const [addTeacherToCourse] = useAddExistingTeacherMutation();
	const [addStudentToCourse] = useAddStudentsToCourseByAdminMutation();
	const [addSchoolAdminToSchool] = useAddUserToSchoolMutation();

	const showWelcomeEmailToggle =
		userToCreateInfo?.welcomeMailFeature &&
		!isHigherThanDistrictAdmin(userToCreateInfo.roleToCreate);

	useEffect(() => {
		if (
			!fName ||
			!lName ||
			(dateOfBirth && dateOfBirth > todayDate && roleToCreate === UserRole.Writer) ||
			(!grade && roleToCreate === UserRole.Writer) ||
			(!externalId && roleToCreate === UserRole.Writer) ||
			(!email && userToCreateInfo?.isEmailAddressRequired) ||
			(!userToCreateInfo?.useCredentialsGeneratorFeature &&
				!useGenerateCredentials &&
				(!userName || !password || !confirmPassword))
		) {
			setShowBadDataWarning(true);
			setDisableSave(true);
		} else {
			setShowBadDataWarning(false);
			setDisableSave(false);
		}
	}, [
		roleToCreate,
		fName,
		lName,
		userName,
		dateOfBirth,
		grade,
		externalId,
		email,
		password,
		confirmPassword,
	]);

	useEffect(() => {
		if (userToCreateInfo && !props.hideDistrictSchool) {
			if (userToCreateInfo.district && !props.districtId) {
				setSelectedDistrict(userToCreateInfo.district.id);
			}
			if (userToCreateInfo.school && !props.schoolId) {
				setSelectedSchool(userToCreateInfo.school.id);
			}
		}
	}, [userToCreateInfo]);

	useEffect(() => {
		const genderOptions: SimpleOption[] = [{ value: "", label: "--" }];
		Object.entries(Gender).map(([key, value]) => {
			genderOptions.push({ value: key, label: value });
		});
		setGenderOptions(genderOptions);

		const ethnicityOptions: SimpleOption[] = [{ value: "", label: "--" }];
		Object.entries(Ethnicity).map(([key, value]) => {
			ethnicityOptions.push({ value: key, label: value });
		});
		setEthnicityOptions(ethnicityOptions);
	}, []);

	useEffect(() => {
		if (districts) {
			const options = districts.map((d) => ({ value: d.id, label: d.name }) as SimpleOption);
			options.unshift({ value: "", label: "None" } as SimpleOption);

			setDistrictOptions(options);
		}
	}, [districts]);

	useEffect(() => {
		// school admins can not select a school since it is a readonly field and district admins don't need this field at all when being created so skip
		if (currentUserRole !== UserRole.SchoolAdmin && roleToCreate !== UserRole.DistrictAdmin) {
			callGetSchoolsByDistrict({
				districtId: selectedDistrict === 0 ? undefined : selectedDistrict,
				filterInactive:
					props.schoolId && roleToCreate === UserRole.SchoolAdmin ? false : true, // allow inactive schools to be found for when adding a school admin from the AddSchoolAdmin route
			})
				.unwrap()
				.then((schools) => {
					if (schools) {
						setSchools(schools);
						const options = schools.map(
							(s) => ({ value: s.id, label: s.name }) as SimpleOption
						);
						options.unshift({ value: 0, label: "None" } as SimpleOption);

						setSchoolOptions(options);
					}
				});
		}
	}, [selectedDistrict, roleToCreate]);

	useEffect(() => {
		const gradeOptions: SimpleOption[] =
			grades?.map((grade) => {
				return {
					value: grade.gradeId,
					label: grade.name,
				} as SimpleOption;
			}) ?? [];
		setGradeOptions(gradeOptions);
	}, [grades]);

	function toggleShowDemographicSection() {
		setShowDemographicSection(!showDemographicSection);
	}

	function updateModel(): CreateUserViewModel | undefined {
		if (userToCreateInfo) {
			const model: CreateUserViewModel = {
				...userToCreateInfo,
				disabled: !active,
				allowUserImages,
				district: districts?.find((d) => d.id == selectedDistrict),
				school:
					currentUserRole === UserRole.SchoolAdmin
						? userToCreateInfo.school
						: schools.find((s) => s.id == selectedSchool),
				fName,
				lName,
				externalID: externalId,
				emailAddress: email,
				sendWelcomeMail: sendWelcomeEmail,
				phone,
				dateOfBirth,
				gradeId: grade,
				ethnicity,
				gender,
				economicallyDisadvantaged,
				disability,
				limitedEnglish,
				generateUserNameAndPassword:
					roleToCreate === UserRole.Writer ? useGenerateCredentials : false,
				userName,
				password,
				confirmPassword,
			};
			return model;
		}
	}

	function handleSave() {
		const model = updateModel();
		if (model) {
			createUser(model)
				.unwrap()
				.then((payload) => {
					// If courseId is provided (from AddUserToCourse), add the newly created user to the course
					if (props.courseId && payload.userId) {
						const mutation =
							model.roleToCreate === UserRole.Proctor
								? addTeacherToCourse({
										courseId: props.courseId,
										userId: payload.userId,
									})
								: addStudentToCourse({
										courseId: props.courseId,
										userIds: [payload.userId],
									});

						mutation
							.unwrap()
							.then(() => {
								handleSuccessfulAdd(payload.successMessage);
							})
							.catch((error) => {
								setErrorMessage(error.data || "Failed to add user to course");
							});
					}
					// If schoolId is provided (from AddSchoolAdmin), add the newly created user to the school
					else if (props.schoolId && payload.userId) {
						addSchoolAdminToSchool({
							schoolId: props.schoolId,
							userId: payload.userId,
						})
							.unwrap()
							.then(() => {
								handleSuccessfulAdd(payload.successMessage);
							})
							.catch((error) => {
								setErrorMessage(error.data || "Failed to add user to school");
							});
					} else {
						handleSuccessfulAdd(payload.successMessage);
					}
				})
				.catch((error) => {
					setErrorMessage(error.data);
				});
		}
	}

	function handleSuccessfulAdd(message: string) {
		if (props.setSuccessMessage) props.setSuccessMessage(message);
		props.onClose();
	}

	function handleCancel() {
		props.onClose();
	}

	return (
		<section>
			<Loading loading={createUserInfoResult.isLoading}></Loading>
			{userToCreateInfo && currentUserRole && (
				<>
					<div>
						{showBadDataWarning && (
							<AlertMessage
								className="mt-3 text-sm"
								type={AlertLevel.Warning}
								message={`Please ensure all required fields are complete${roleToCreate === UserRole.Writer ? " and that the date of birth is today or earlier" : ""}.`}
								showIcon={true}
							/>
						)}
						{errorMessage && (
							<AlertMessage
								className="text-sm mt-4 py-2 !px-4"
								type={AlertLevel.Error}
								message={errorMessage}
								showIcon={true}
							/>
						)}
						<div className="flex justify-between items-end">
							<h3 className="mt-2">User Information</h3>
							<p className="text-xs leading-6 text-gray-600">
								<RequiredField className="!text-sm" /> - Denotes a required field
							</p>
						</div>
						<div className="pb-2 pt-2 grid grid-cols-2 gap-y-2 gap-x-3">
							<div className="grid grid-cols-8 gap-3 col-span-2">
								{roleOptions ? (
									<Field className="col-span-4">
										<Select
											value={roleToCreate}
											setValue={(v) => setRoleToCreate(v as string)}
											label="Role"
											simpleListBoxId="role"
											requiredField
											options={roleOptions}
											fieldClasses="md:pt-0"
											simpleListBoxClasses="w-full"
										/>
									</Field>
								) : (
									<Field className="col-span-4">
										<Label htmlFor="role" className="font-medium text-sm">
											Role
											<RequiredField />
										</Label>
										<div className="relative">
											{roleColors[props.selectedRole as UserRole] && (
												<span
													className={`absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full ${roleColors[props.selectedRole as UserRole]}`}></span>
											)}
											<Input
												type="text"
												name="role"
												id="role"
												readOnly
												className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600 pl-8"
												value={
													roleDescriptions[
														props.selectedRole as UserRole
													] ?? ""
												}
											/>
										</div>
									</Field>
								)}
								<Field className="col-span-2">
									<Label htmlFor="activeSwitch" className="font-medium text-sm">
										Status
									</Label>
									<div className="mt-2">
										<div className="flex items-center ml-2">
											<Label className="sr-only">Activated Toggle</Label>
											<Switch
												checked={active}
												onChange={setActive}
												name="activeSwitch"
												className="group relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 data-[checked]:bg-primary-600">
												<span
													aria-hidden="true"
													className="pointer-events-none inline-block size-6 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
												/>
											</Switch>

											<div className="ml-2 flex gap-1 items-center text-sm">
												{active ? (
													<CheckCircleIcon className="size-7 text-green-500" />
												) : (
													<XCircleIcon className="size-7 text-red-500" />
												)}
												<p
													className={`font-semibold ${active ? "text-green-700" : "text-red-700"}`}>
													{active ? "Active" : "Inactive"}
												</p>
											</div>
										</div>
									</div>
								</Field>
								{!config?.isUtahComposeSite && (
									<Field className="col-span-2">
										<Label htmlFor="userAvatar" className="font-medium text-sm">
											Allow User Avatars
										</Label>
										<div className="mt-2 ml-2">
											<Switch
												checked={allowUserImages}
												onChange={setAllowUserImages}
												name="userAvatar"
												className="group relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 data-[checked]:bg-primary-600">
												<span
													aria-hidden="true"
													className="pointer-events-none inline-block size-6 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
												/>
											</Switch>
										</div>
									</Field>
								)}
							</div>
							{!props.hideDistrictSchool &&
								!isHigherThanDistrictAdmin(roleToCreate as UserRole) && (
									<>
										{currentUserRole != UserRole.SchoolAdmin &&
											(isHigherThanDistrictAdmin(currentUserRole) ? (
												<Field
													className={`${roleToCreate === UserRole.DistrictAdmin ? "col-span-2" : "col-span-1"}`}>
													<Select
														value={selectedDistrict}
														setValue={(v) =>
															setSelectedDistrict(v as number)
														}
														label="District"
														simpleListBoxId="districtFilter"
														requiredField
														options={districtOptions}
														fieldClasses="w-full md:flex-1"
														simpleListBoxClasses="w-full"
													/>
												</Field>
											) : (
												<Field
													className={`${roleToCreate === UserRole.DistrictAdmin ? "col-span-2" : "col-span-1"}`}>
													<Label
														htmlFor="district"
														className="font-medium text-sm">
														District
														<RequiredField />
													</Label>
													<Input
														type="text"
														name="district"
														id="district"
														readOnly
														className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
														value={
															userToCreateInfo.district?.name ?? ""
														}
													/>
												</Field>
											))}
										{roleToCreate != UserRole.DistrictAdmin &&
											(currentUserRole != UserRole.SchoolAdmin ? (
												<Field>
													<Select
														value={selectedSchool}
														setValue={(v) =>
															setSelectedSchool(v as number)
														}
														label="School"
														simpleListBoxId="schoolFilter"
														requiredField
														options={schoolOptions}
														fieldClasses="w-full md:flex-1 pt-2 md:pt-0"
														simpleListBoxClasses="w-full"
													/>
												</Field>
											) : (
												<Field className="col-span-2">
													<Label
														htmlFor="school"
														className="font-medium text-sm">
														School
														<RequiredField />
													</Label>
													<Input
														type="text"
														name="school"
														id="school"
														readOnly
														className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
														value={userToCreateInfo.school?.name ?? ""}
													/>
												</Field>
											))}
									</>
								)}
							<Field>
								<Label htmlFor="first-name" className="font-medium text-sm">
									First Name
									<RequiredField />
								</Label>
								<Input
									type="text"
									name="first-name"
									id="first-name"
									className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
									required
									maxLength={userToCreateInfo.rules.firstNameMaxLength}
									value={fName}
									onChange={(e) => setFName(e.target.value)}
								/>
							</Field>
							<Field>
								<Label htmlFor="last-name" className="font-medium text-sm">
									Last Name
									<RequiredField />
								</Label>
								<Input
									type="text"
									name="last-name"
									id="last-name"
									className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
									required
									maxLength={userToCreateInfo.rules.lastNameMaxLength}
									value={lName}
									onChange={(e) => setLName(e.target.value)}
								/>
							</Field>

							{roleToCreate === UserRole.Writer && (
								<div className="grid grid-cols-3 gap-3 col-span-2">
									{!isAdminRole(roleToCreate as UserRole) && (
										<Field>
											<Label
												htmlFor="external-id"
												className="font-medium text-sm">
												{externalIdLabel(roleToCreate as UserRole, config)}
												<RequiredField />
											</Label>
											<Input
												type="text"
												name="external-id"
												id="external-id"
												required
												className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
												maxLength={
													userToCreateInfo.rules.externalIdMaxLength
												}
												value={externalId}
												onChange={(e) => setExternalId(e.target.value)}
											/>
										</Field>
									)}
									<Field>
										<Label htmlFor="dob" className="font-medium text-sm">
											Date of Birth
										</Label>
										<Input
											type="date"
											name="dob"
											id="dob"
											className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
											value={dateOfBirth}
											max={todayDate}
											onChange={(e) => setDateOfBirth(e.target.value)}
										/>
									</Field>
									<Field>
										<Select
											value={grade}
											setValue={(v) => setGrade(v as number)}
											label="Grade"
											requiredField
											prompt="Pick a grade..."
											simpleListBoxId="grade"
											options={gradeOptions}
											fieldClasses="md:pt-0"
											simpleListBoxClasses="w-full"
										/>
									</Field>
								</div>
							)}
							{roleToCreate !== UserRole.Writer && (
								<div className="grid grid-cols-7 gap-3 col-span-2">
									{!isAdminRole(roleToCreate as UserRole) && (
										<Field className="col-span-full">
											<Label
												htmlFor="external-id"
												className="font-medium text-sm">
												{externalIdLabel(roleToCreate as UserRole, config)}
											</Label>
											<Input
												type="text"
												name="external-id"
												id="external-id"
												className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
												maxLength={
													userToCreateInfo.rules.externalIdMaxLength
												}
												value={externalId}
												onChange={(e) => setExternalId(e.target.value)}
											/>
										</Field>
									)}
									<Field
										className={`${showWelcomeEmailToggle ? "col-span-3" : "col-span-5"}`}>
										<Label htmlFor="email" className="font-medium text-sm">
											Email
											{userToCreateInfo.isEmailAddressRequired && (
												<RequiredField />
											)}
										</Label>
										<Input
											id="email"
											name="email"
											type="text"
											required={userToCreateInfo.isEmailAddressRequired}
											className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
											maxLength={userToCreateInfo.rules.emailMaxLength}
											value={email}
											onChange={(e) => setEmail(e.target.value)}
										/>
									</Field>

									<div
										className={`${showWelcomeEmailToggle ? "col-span-4" : "col-span-2"} flex gap-x-3`}>
										{showWelcomeEmailToggle && (
											<Field className="w-full">
												<Label className="font-medium text-sm">
													Send Welcome Email
												</Label>
												<div className="flex items-center mt-2 ml-2 gap-x-3">
													<Switch
														checked={sendWelcomeEmail}
														onChange={setSendWelcomeEmail}
														name="includeInReports"
														className="group relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 data-[checked]:bg-primary-600">
														<span
															aria-hidden="true"
															className="pointer-events-none inline-block size-6 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
														/>
													</Switch>
													<InformationCircleIcon
														data-tooltip-id="welcome-email-info"
														className="size-7 text-primary-700 cursor-pointer"
													/>
													<Tooltip
														id="welcome-email-info"
														place="bottom-start"
														className="!w-56"
														content="This letter will include information for new users to get started using the program, such as login credentials and how to navigate the site."></Tooltip>
												</div>
											</Field>
										)}
										<Field className="w-full">
											<Label htmlFor="phone" className="font-medium text-sm">
												Phone
											</Label>
											<Input
												id="phone"
												name="phone"
												type="text"
												className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
												maxLength={userToCreateInfo.rules.phoneMaxLength}
												value={phone}
												onChange={(e) => setPhone(e.target.value)}
											/>
										</Field>
									</div>
								</div>
							)}
						</div>
					</div>
					{roleToCreate === UserRole.Writer &&
						(!config?.isUtahComposeSite ||
							(config?.isUtahComposeSite &&
								isHigherThanDistrictAdmin(currentUserRole))) && (
							<div>
								<div className="flex items-center">
									{showDemographicSection ? (
										<ChevronDownIcon
											tabIndex={0}
											className="size-5 hover:cursor-pointer"
											onClick={toggleShowDemographicSection}
											onKeyDown={(e) =>
												handleEnterSpaceKey(e, toggleShowDemographicSection)
											}
										/>
									) : (
										<ChevronRightIcon
											tabIndex={0}
											className="size-5 hover:cursor-pointer"
											onClick={toggleShowDemographicSection}
											onKeyDown={(e) =>
												handleEnterSpaceKey(e, toggleShowDemographicSection)
											}
										/>
									)}
									<h3 className="text-base font-semibold text-gray-900 pt-2 pb-2 ml-4">
										Demographic Information
									</h3>
								</div>
								{showDemographicSection && (
									<div className="pb-2 pt-1 grid grid-cols-2 gap-y-2 gap-x-3">
										<div className="grid grid-cols-8 gap-3 col-span-2">
											<Field className="col-span-3">
												<Select
													value={ethnicity}
													setValue={(v) => setEthnicity(v as string)}
													label="Ethnicity"
													simpleListBoxId="ethnicity"
													options={ethnicityOptions}
													fieldClasses="md:pt-0"
													simpleListBoxClasses="w-full"
												/>
											</Field>
											<Field className="col-span-3">
												<Select
													value={gender}
													setValue={(v) => setGender(v as string)}
													label="Gender"
													simpleListBoxId="gender"
													options={genderOptions}
													fieldClasses="md:pt-0"
													simpleListBoxClasses="w-full"
												/>
											</Field>
											<Field className="col-span-2">
												<Label
													htmlFor="disability"
													className="font-medium text-sm">
													Disability
												</Label>
												<Input
													type="number"
													name="disability"
													id="disability"
													className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
													placeholder="--"
													value={disability}
													onChange={(e) =>
														setDisability(Number(e.target.value))
													}
												/>
											</Field>
										</div>
										<Field>
											<Select
												value={economicallyDisadvantaged?.toString() ?? ""}
												setValue={(v) =>
													setEconomicallyDisadvantaged(
														v === "" ? undefined : v === "true"
													)
												}
												label="Economically Disadvantaged"
												simpleListBoxId="economicallyDisadvantaged"
												options={YesNoOptions}
												fieldClasses="md:pt-0"
												simpleListBoxClasses="w-full"
											/>
										</Field>
										<Field>
											<Select
												value={limitedEnglish?.toString() ?? ""}
												setValue={(v) =>
													setLimitedEnglish(
														v === "" ? undefined : v === "true"
													)
												}
												label="Limited English"
												simpleListBoxId="limited-english"
												options={YesNoOptions}
												fieldClasses="md:pt-0"
												simpleListBoxClasses="w-full"
											/>
										</Field>
									</div>
								)}
							</div>
						)}
					{(isHigherThanDistrictAdmin(roleToCreate as UserRole) ||
						!userToCreateInfo.useCredentialsGeneratorFeature) && (
						<section>
							<hr className="my-2" />
							<h3>Login Information</h3>
							<div className="pb-2 pt-2 grid grid-cols-2 gap-y-2 gap-x-3">
								{roleToCreate === UserRole.Writer && (
									<Field className="ml-2 flex items-center gap-x-4 col-span-2">
										<Input
											id="generateCredentialsCheckbox"
											type="checkbox"
											checked={useGenerateCredentials}
											className="size-4 rounded-sm border-2 border-gray-300 text-primary-600 focus:ring-primary-600"
											onChange={() =>
												setUseGenerateCredentials(!useGenerateCredentials)
											}
										/>
										<Description className="text-sm">
											Generate Username and Password
										</Description>
										<InformationCircleIcon
											data-tooltip-id="login-info"
											className="size-6 text-primary-700 cursor-pointer"
										/>
										<Tooltip
											id="login-info"
											place="right"
											className="!w-72 z-10">
											<div>
												<p>
													When Generate Username and Password is checked,
												</p>
												<p className="mt-2">
													1. the Username will be first initial, last name
													(up to 7 characters) and a 2 digit number
													and{" "}
												</p>
												<p>
													2. the Password will be the external ID (all
													lowercase). If that is less than{" "}
													{userToCreateInfo.rules.writerPasswordMinLength}{" "}
													characters, “writing” will be added onto the
													end.{" "}
												</p>
												<p className="mt-2">
													When Generate Username and Password is
													unchecked, the Username and Password can be
													entered manually.
												</p>
											</div>
										</Tooltip>
									</Field>
								)}
								{(!useGenerateCredentials || roleToCreate !== UserRole.Writer) && (
									<>
										<Field className="col-span-2">
											<Label
												htmlFor="username"
												className="font-medium text-sm">
												Username
												<RequiredField />
											</Label>
											<Input
												type="text"
												name="username"
												id="username"
												className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
												required
												minLength={userToCreateInfo.rules.userNameMinLength}
												maxLength={userToCreateInfo.rules.userNameMaxLength}
												value={userName}
												onChange={(e) => setUserName(e.target.value)}
											/>
										</Field>
										<Field>
											<Label
												htmlFor="newPassword"
												className="font-medium text-sm">
												Password <RequiredField />
											</Label>
											<Password
												id="new-password"
												name="new-password"
												minLength={
													roleToCreate === UserRole.Writer
														? userToCreateInfo.rules
																.writerPasswordMinLength
														: userToCreateInfo.rules
																.nonWriterPasswordMinLength
												}
												maxLength={userToCreateInfo.rules.passwordMaxLength}
												placeholder="new password"
												autoComplete="new-password"
												className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
												value={password}
												onChange={(e) => setPassword(e.target.value)}
											/>
										</Field>
										<Field>
											<Label
												htmlFor="confirmPassword"
												className="font-medium text-sm">
												Confirm Password <RequiredField />
											</Label>
											<Password
												id="confirm-password"
												name="confirm-password"
												minLength={
													roleToCreate === UserRole.Writer
														? userToCreateInfo.rules
																.writerPasswordMinLength
														: userToCreateInfo.rules
																.nonWriterPasswordMinLength
												}
												maxLength={userToCreateInfo.rules.passwordMaxLength}
												placeholder="confirm password"
												autoComplete="confirm-pass"
												className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
												value={confirmPassword}
												onChange={(e) => setConfirmPassword(e.target.value)}
											/>
										</Field>
									</>
								)}
							</div>
						</section>
					)}
				</>
			)}
			<div className="mt-4 grid grid-cols-2 gap-3">
				<Button
					id="cancel-button"
					onClick={handleCancel}
					onKeyDown={(e) => handleEnterKey(e, handleCancel)}
					className="btn btn-neutral">
					Cancel
				</Button>
				<Button
					id="confirm-button"
					disabled={disableSave}
					onClick={handleSave}
					onKeyDown={(e) => handleEnterKey(e, handleSave)}
					className="btn btn-primary">
					{props.saveButtonText ?? "Save"}
				</Button>
			</div>
		</section>
	);
}
